import React, { useState } from 'react';
import useDidUpdateEffect from '@use-effect/did-update';
import slugify from 'slugify';
import { useSpring } from '@react-spring/web';
import { Switches, Cross } from '@solent-university/solent-icons';

import Section from 'Elements/Section';
import SearchInput from 'Elements/Input/Search';
import Toggle, { ToggleProps } from 'Elements/Input/Toggle';
import Hidden from 'Elements/Hidden';

import ListBlock from 'Components/Block/List';
import Grid, { Col } from 'Components/Grid';

import Select from 'Elements/Input/Dropdown';
import { StyledLabel } from 'Elements/Input/Dropdown/Dropdown.styled';

import useFilters, { type ActiveFilter, type FilterGroup } from 'Common/hooks/useFilters';

import {
    Filters,
    SelectWrapper,
    ToggleWrapper,
    SearchWrapper,
    StyledIcon,
    FilterPillButton,
    InfoWrapper,
    PillWrapper,
    ModalPillWrapper,
    FilterButton,
    Icon,
    MobileMenu,
    Modal,
    MobileButtonWrapper,
    StyledHr,
    HeadingWrapper,
    Heading,
    CloseButton,
    ResultsText,
    ResultsNumber
} from './CourseListing.styled';

export interface Props {
    id?: string;
    filters: FilterGroup[];
    toggle: ToggleProps;
    initialActiveFilters: ActiveFilter[];
    initialSearchTerm?: string;
    numberOfResults?: number;
}

const CourseListing: React.FC<Props> = props => {
    const { id, filters, toggle, initialActiveFilters, initialSearchTerm, numberOfResults } = props;

    const [activeYear, setActiveYear] = useState<string>(toggle.defaultValue as string);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    const mobileSpring = useSpring({
        left: mobileMenuOpen ? '0' : '+100%',
        right: mobileMenuOpen ? '0%' : '-100%',
        config: { mass: 1, tension: 180, friction: 20 }
    });

    const { state, actions, ui } = useFilters(initialActiveFilters, initialSearchTerm);
    const { activeFilters, searchTerm } = state;
    const { setSearchTerm, removeFilter } = actions;
    const { generateDropdownOptions } = ui;

    useDidUpdateEffect(() => {
        actions.filterResults([{ parameter: 'year', value: activeYear }]);
    }, [activeFilters, searchTerm, activeYear]);

    const searchBoxJSX = (
        <SearchWrapper>
            <StyledLabel htmlFor="course-search">Course search:</StyledLabel>
            <SearchInput
                id="course-search"
                aria-label="Course text search"
                button={{ ariaLabel: 'Submit search for courses' }}
                handleSubmit={value => setSearchTerm(value)}
                name="Course search"
                defaultValue={initialSearchTerm ? initialSearchTerm : undefined}
                placeholder="Search courses"
                variant="button"
            />
        </SearchWrapper>
    );

    const filtersJSX = (
        <>
            {filters.map(filter => (
                <SelectWrapper key={filter.type} $type={filter.type}>
                    <Select
                        button={{
                            ariaLabel: filter.label,
                            label: filter.label
                        }}
                        id={filter.type}
                    >
                        <ListBlock
                            fieldset={true}
                            id={`fieldset-${slugify(filter.type, {
                                lower: true
                            })}`}
                            maxHeight={filter.options.length > 8}
                        >
                            {generateDropdownOptions(filter)}
                        </ListBlock>
                    </Select>
                </SelectWrapper>
            ))}
            <ToggleWrapper>
                <StyledLabel htmlFor={toggle.id}>Course year:</StyledLabel>
                <Toggle {...toggle} handleValueChange={(year: string) => setActiveYear(year)} />
            </ToggleWrapper>
        </>
    );

    const pillsJSX = (
        <>
            {searchTerm && (
                <FilterPillButton
                    onClick={() => setSearchTerm(undefined)}
                    aria-label={`Remove ${searchTerm} course filter`}
                >
                    Result search: {searchTerm}
                    <StyledIcon />
                </FilterPillButton>
            )}
            {activeFilters.map(item => (
                <React.Fragment key={`filter-${item.id}`}>
                    <FilterPillButton
                        onClick={() => removeFilter(item.id)}
                        aria-label={`Remove ${item.id} course filter`}
                    >
                        {`${item.label}`}
                        <StyledIcon />
                    </FilterPillButton>
                </React.Fragment>
            ))}
        </>
    );

    return (
        <Section id="filters" paddingTop={0.5} paddingBottom="none" background>
            <form onSubmit={e => e.preventDefault()} id={id}>
                <Hidden defaultHidden={true} medium={false} renderHTML={false}>
                    <Grid>
                        <Col>
                            <Filters>
                                {searchBoxJSX}
                                {filtersJSX}
                            </Filters>
                        </Col>
                    </Grid>
                </Hidden>
                <Hidden base={false} medium={true} renderHTML={false}>
                    <Grid>
                        <Col>{searchBoxJSX}</Col>
                        <Col>
                            <FilterButton
                                aria-label="Click to access mobile filter menu"
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                aria-expanded={mobileMenuOpen}
                                aria-controls="mobile-menu"
                            >
                                <Icon as={Switches} />
                                Filter courses
                            </FilterButton>
                        </Col>
                    </Grid>
                </Hidden>
                <Grid>
                    <Col>
                        <InfoWrapper>
                            <PillWrapper>{pillsJSX}</PillWrapper>
                            <ResultsText>
                                Showing
                                <ResultsNumber> {numberOfResults} courses </ResultsNumber>
                                that fit your criteria
                            </ResultsText>
                        </InfoWrapper>
                    </Col>
                </Grid>
                {mobileMenuOpen && (
                    <Modal style={mobileSpring} id="mobile-menu">
                        <MobileMenu>
                            <HeadingWrapper>
                                <CloseButton
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                    aria-label="Click to close mobile menu"
                                    aria-controls="mobile-menu"
                                    aria-expanded={mobileMenuOpen}
                                >
                                    <Icon as={Cross} />
                                </CloseButton>
                                <Heading>Filter courses</Heading>
                            </HeadingWrapper>
                            <Grid>
                                <Col>
                                    {filtersJSX}
                                    <StyledHr />
                                    <ModalPillWrapper>{pillsJSX}</ModalPillWrapper>
                                </Col>
                            </Grid>
                            <MobileButtonWrapper>
                                <FilterButton
                                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                    aria-label="Click to apply filters and close mobile menu"
                                    aria-controls="mobile-menu"
                                    aria-expanded={mobileMenuOpen}
                                >
                                    <Icon as={Switches} />
                                    Apply Filter
                                </FilterButton>
                            </MobileButtonWrapper>
                        </MobileMenu>
                    </Modal>
                )}
            </form>
        </Section>
    );
};

export default CourseListing;
